import React, { useState } from "react";
import { useParams } from "react-router-dom";
import SkillsInput from "../../../../../components/skillsInput/skillsInput";
import validateRoleDescription from "../../../../../components/codiOS/utils/validateRoleDescription";
import { validateWithCodi } from "../../../../../components/codiOS/utils/validateWithCodi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OpenApplicationsCard = ({ onRoleAdded, projectTitle }) => {
  const { projectId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [newRole, setNewRole] = useState({
    title: "",
    description: "",
    timeCommitment: "",
    skills: "",
    question1: "",
    question2: "",
  });
  const [errors, setErrors] = useState({});

  const toggleApplications = () => setIsOpen((prev) => !prev);

  const handleInputChange = (e) => {
    setNewRole({ ...newRole, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: null }));
  };

  const handleAddRole = async () => {
    const newErrors = {};

    if (!newRole.title.trim()) {
      newErrors.title = "Role title is required.";
    }

    const descLength = validateRoleDescription(newRole.description || "");
    const descMeaning = await validateWithCodi(newRole.description || "");

    if (!descLength.valid) {
      newErrors.description =
        descLength.reason === "too short"
          ? "Description is too short."
          : "Description is too long.";
    } else if (!descMeaning.valid) {
      newErrors.description = descMeaning.reason || "Description lacks clarity.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/applications/${projectId}/add-role`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          roleTitle: newRole.title,
          description: newRole.description,
          timeCommitment: newRole.timeCommitment || null,
          skills: newRole.skills || "",
          questions: [newRole.question1, newRole.question2].filter(Boolean),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert("Role added successfully!");
        localStorage.setItem(
          "justOpenedRoleNoti",
          JSON.stringify({
            roleTitle: newRole.title,
            projectTitle: projectTitle || "Untitled Project",
          })
        );

        setNewRole({
          title: "",
          description: "",
          timeCommitment: "",
          skills: "",
          question1: "",
          question2: "",
        });
        setErrors({});
        window.location.reload();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error adding role:", error);
    }
  };

  return (
    <div className="w-full bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Collaborations</h2>
        <button
          onClick={toggleApplications}
          className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors duration-200 ${
            isOpen ? "bg-blue-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block w-5 h-5 transform bg-white rounded-full shadow-md transition-transform duration-200 ${
              isOpen ? "translate-x-5" : "translate-x-1"
            }`}
          ></span>
        </button>
      </div>

      {isOpen && (
        <div className="flex flex-col space-y-3">
          {/* Role Title */}
          <input
            type="text"
            name="title"
            value={newRole.title}
            onChange={handleInputChange}
            placeholder="Role (e.g., UI/UX Designer)"
            className={`w-full rounded-lg px-4 py-2 text-sm outline-none transition ${
              errors.title
                ? "bg-red-50 border border-red-500 focus:ring-red-500"
                : "bg-gray-50 border border-gray-300 focus:ring-blue-500"
            }`}
          />
          {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}

          {/* Time Commitment */}
          <input
            type="number"
            name="timeCommitment"
            min="1"
            value={newRole.timeCommitment}
            onChange={handleInputChange}
            placeholder="⏱️ Time Commitment (hrs/week)"
            className="w-full rounded-lg px-4 py-2 text-sm border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Skills */}
          <SkillsInput
            skills={newRole.skills ? newRole.skills.split(",").map((s) => s.trim()).filter(Boolean) : []}
            setSkills={(arr) =>
              setNewRole((prev) => ({ ...prev, skills: arr.join(",") })) // 👈 still store as comma-separated
            }
          />


          {/* Role Description */}
          <textarea
            name="description"
            value={newRole.description}
            onChange={handleInputChange}
            placeholder="📄 Role description"
            className={`w-full rounded-lg px-4 py-2 text-sm min-h-[80px] resize-none outline-none transition ${
              errors.description
                ? "bg-red-50 border border-red-500 focus:ring-red-500"
                : "bg-gray-50 border border-gray-300 focus:ring-blue-500"
            }`}
          />
          {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}

          {/* Questions */}
          <input
            type="text"
            name="question1"
            value={newRole.question1}
            onChange={handleInputChange}
            placeholder="❓ Question 1"
            className="w-full rounded-lg px-4 py-2 text-sm border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            name="question2"
            value={newRole.question2}
            onChange={handleInputChange}
            placeholder="❓ Question 2"
            className="w-full rounded-lg px-4 py-2 text-sm border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Add Role */}
          <button
            onClick={handleAddRole}
            className="w-full bg-black text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-900 transition duration-200"
          >
            Add Role
          </button>
        </div>
      )}
    </div>
  );
};

export default OpenApplicationsCard;
